/**
 * Core-100 R4 (CORE-AW): partial-day absences as capacity input.
 *
 * `technician_absence_intervals` records a manager's "away 10:00-12:00 on this date". Capacity
 * (`resolveTechnicianCapacity` → `unavailableMinutesInShift`) subtracts the intervals found on
 * `tech.absence_intervals`; these helpers put them there. Only the times are carried — the reason
 * column is never read by capacity surfaces.
 */

export interface AbsenceIntervalRow {
  technician_id: string;
  absence_date: string;
  start_time: string;
  end_time: string;
}

/** The only columns a capacity surface reads (no reason, no author). */
export const ABSENCE_INTERVAL_CAPACITY_COLUMNS = 'technician_id, absence_date, start_time, end_time';

export type AbsenceIntervalsByTech = Map<string, Array<Omit<AbsenceIntervalRow, 'technician_id'>>>;

export function groupAbsenceIntervals(rows: readonly AbsenceIntervalRow[] | null | undefined): AbsenceIntervalsByTech {
  const byTech: AbsenceIntervalsByTech = new Map();
  for (const r of rows || []) {
    if (!r?.technician_id) continue;
    const list = byTech.get(r.technician_id) ?? [];
    list.push({ absence_date: String(r.absence_date).slice(0, 10), start_time: r.start_time, end_time: r.end_time });
    byTech.set(r.technician_id, list);
  }
  return byTech;
}

/**
 * Returns technicians carrying their absence intervals. A technician with none gets an empty list, so the
 * result is the same as before for them. Input rows are not mutated.
 */
export function withAbsenceIntervals<T extends { id?: string | null }>(
  technicians: readonly T[] | null | undefined,
  byTech: AbsenceIntervalsByTech,
): Array<T & { absence_intervals: Array<Omit<AbsenceIntervalRow, 'technician_id'>> }> {
  return (technicians || []).map((t) => ({ ...t, absence_intervals: byTech.get(String(t?.id ?? '')) ?? [] }));
}
